import { useEffect, useMemo, useRef, useState } from "react";
import {
  Plus, PlusCircle, Loader2, RefreshCw, PlayCircle, CheckCircle2, Clock, PenTool,
  AlertTriangle, Radio, Search, X as XIcon, ChevronRight, Layers, ClipboardList,
  Check, Users, Flag, Sun, Moon, History, Trash2,
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { todayLocalIso } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { useSiteShift } from "@/hooks/useSiteShift";
import { useActiveSwing } from "@/hooks/useActiveSwing";
import { Link, useNavigate } from "react-router-dom";
import CustomJobModal from "@/components/CustomJobModal";
import AssetDemandRow from "@/components/AssetDemandRow";
import AssetDemandSignoffModal from "@/components/AssetDemandSignoffModal";

/**
 * SWING — the ONLY home page.
 *
 * Shows the inspections your crew is currently running (this shift) with a
 * live progress bar for each. One "Action" button in the top-right opens the
 * unified create modal (custom job or a fresh inspection swing).
 *
 * Mirrors the mobile-app layout: nothing else on this screen.
 */
export default function Swing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [inspections, setInspections] = useState(null);
  const [demands, setDemands] = useState([]); // OPEN custom jobs (asset-demands)
  const [doneDemands, setDoneDemands] = useState([]); // DONE custom jobs (from shift-summary)
  const [signoffDemand, setSignoffDemand] = useState(null);
  const [deleteDemand, setDeleteDemand] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [showAction, setShowAction] = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const [showInspSwing, setShowInspSwing] = useState(false);
  const [startShift, setStartShift] = useState(null); // preset when opening the picker from the Start Swing hero
  const [showFinishConfirm, setShowFinishConfirm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // inspection to delete
  const [deleting, setDeleting] = useState(false);
  const [showShiftSwapConfirm, setShowShiftSwapConfirm] = useState(false);
  const [swappingShift, setSwappingShift] = useState(false);
  // If we click Swap-to-Night while another crew is already on Night at this
  // site, capture their swing here so we can prompt "close their swing to
  // hand over" before starting ours. Josh (Feb 27): "if there is another
  // crew on nights just maybe show another screen confirming that that crew
  // has finished there swing".
  const [otherCrewSwing, setOtherCrewSwing] = useState(null);

  const canSupervise = useMemo(() => {
    if (!user) return false;
    if (user.is_admin) return true;
    const r = String(user.role || "").toLowerCase();
    return /admin|control|owner|supervisor|superintendent|planner/.test(r);
  }, [user]);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/inspections/${deleteTarget.id || deleteTarget._id}`);
      setDeleteTarget(null);
      await reload();
    } catch (ex) {
      setErr(apiError(ex));
    } finally {
      setDeleting(false);
    }
  };
  const pollRef = useRef(null);

  const reload = async () => {
    setBusy(true); setErr("");
    try {
      const [insp, dem] = await Promise.all([
        api.get("/inspections"),
        api.get("/asset-demands").catch(() => ({ data: [] })),
      ]);
      const inspArr = Array.isArray(insp.data) ? insp.data : insp.data?.items || insp.data?.results || insp.data?.data || [];
      const demArr = Array.isArray(dem.data) ? dem.data : dem.data?.items || dem.data?.results || dem.data?.data || [];
      setInspections(inspArr);
      setDemands(demArr);
    } catch (ex) {
      setErr(apiError(ex));
      setInspections([]);
    } finally { setBusy(false); }
  };
  useEffect(() => { reload(); return () => { if (pollRef.current) clearInterval(pollRef.current); }; }, []);

  // Live sync — poll every 5s while the tab is visible so the portal mirrors
  // whatever the mobile app is writing (teeth %, pin matrix, statuses).
  const activeCount = useMemo(() => (
    (inspections || []).filter(isActive).length + (demands || []).filter((d) => String(d.status || "").toLowerCase() !== "done").length
  ), [inspections, demands]);
  useEffect(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (activeCount > 0) {
      pollRef.current = setInterval(() => {
        if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
        reload().catch(() => {});
      }, 5000);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [activeCount]);

  // Current-shift filter — sourced from site-config /site-config/me
  const { shift: resolvedShift, ready: shiftReady, dayStart, dayFinish, nightStart, nightFinish } = useSiteShift();
  // Server-side swing (shared across the whole crew) — falls back to the
  // auto-detected shift from site config when no swing is open yet.
  const {
    swing: activeSwing,
    finish: finishSwing,
    refresh: refreshSwing,
    loading: swingLoading,
  } = useActiveSwing(user?.site_code || "__default__", user);
  const shift = (activeSwing && deriveSwingShift(activeSwing)) || resolvedShift;

  // When a server swing is active, the source of truth for "what's on this
  // swing" is `swing.inspection_ids`. Fall back to date heuristics only if
  // the swing has no explicit list (very early after creation).
  const shiftInspections = useMemo(() => {
    const all = inspections || [];
    if (activeSwing) {
      const ids = new Set(activeSwing.inspection_ids || []);
      if (ids.size > 0) {
        return all.filter((it) => ids.has(it.id || it._id)).sort((a, b) => (
          new Date(b.updated_at || b.created_at || 0) - new Date(a.updated_at || a.created_at || 0)
        ));
      }
    }
    return all.filter((it) => isThisShift(it, shift, user)).sort((a, b) => (
      new Date(b.updated_at || b.created_at || 0) - new Date(a.updated_at || a.created_at || 0)
    ));
  }, [inspections, activeSwing, shift, user]);

  const activeList = shiftInspections.filter(isActive);
  const completedList = shiftInspections.filter((it) => !isActive(it));

  // Custom jobs (asset-demands) belonging to the current active swing.
  // The prod `/asset-demands` endpoint only returns records with
  // `status != "done"`, so once a crew signs off a custom job it naturally
  // disappears from the list (matching the mobile-app behaviour). The
  // `completedDemands` filter is kept in case a future server build returns
  // done records too — the Completed section will pick them up seamlessly.
  const activeDemands = useMemo(() => {
    if (!activeSwing) return [];
    return (demands || []).filter((d) =>
      d.swing_id === activeSwing.id &&
      String(d.status || "").toLowerCase() !== "done"
    );
  }, [demands, activeSwing]);
  const completedDemands = useMemo(() => {
    if (!activeSwing) return [];
    // Merge done demands from two possible sources:
    //   1) `demands` (rare — prod's /asset-demands filters these out today,
    //      but a future build could include them, so we still check).
    //   2) `doneDemands` sourced from /shift-summary (the mobile app path).
    const merged = new Map();
    (demands || []).forEach((d) => {
      if (d.swing_id === activeSwing.id && String(d.status || "").toLowerCase() === "done") {
        merged.set(d.id, d);
      }
    });
    (doneDemands || []).forEach((d) => { if (d?.id) merged.set(d.id, d); });
    return Array.from(merged.values()).sort((a, b) => (
      new Date(b.closed_at || b.completed_at || b.signed_at || b.created_at || 0) -
      new Date(a.closed_at || a.completed_at || a.signed_at || a.created_at || 0)
    ));
  }, [demands, doneDemands, activeSwing]);

  // Fetch DONE custom jobs (asset-demands) for this swing. The prod
  // `/asset-demands` endpoint filters out `status=done` records, so we source
  // them from `/shift-summary.custom_jobs` (same as the mobile app).
  // A swing can span multiple shifts (e.g. a day swing where the crew signs
  // off jobs after midnight), so we query BOTH day + night windows across
  // the swing's date range and merge, then filter by swing_id for safety.
  useEffect(() => {
    let cancelled = false;
    const fetchDone = async () => {
      if (!activeSwing?.id) { setDoneDemands([]); return; }
      const dates = new Set([
        activeSwing.start_date,
        activeSwing.end_date,
        todayLocalIso(),
      ].filter(Boolean));
      const queries = [];
      dates.forEach((d) => {
        queries.push(api.get(`/shift-summary?date=${encodeURIComponent(d)}&shift=day`).catch(() => null));
        queries.push(api.get(`/shift-summary?date=${encodeURIComponent(d)}&shift=night`).catch(() => null));
      });
      const results = await Promise.all(queries);
      if (cancelled) return;
      const seen = new Map();
      results.forEach((r) => {
        const list = Array.isArray(r?.data?.custom_jobs) ? r.data.custom_jobs : [];
        list.forEach((d) => {
          if (String(d.status || "").toLowerCase() !== "done") return;
          if (d.swing_id && d.swing_id !== activeSwing.id) return;
          if (d?.id) seen.set(d.id, d);
        });
      });
      setDoneDemands(Array.from(seen.values()));
    };
    fetchDone();
    return () => { cancelled = true; };
  }, [activeSwing?.id, activeSwing?.start_date, activeSwing?.end_date, inspections?.length, demands?.length]);

  const confirmDeleteDemand = async () => {
    if (!deleteDemand) return;
    setDeleting(true);
    try {
      await api.delete(`/asset-demands/${deleteDemand.id}`);
      setDeleteDemand(null);
      await reload();
    } catch (ex) {
      setErr(apiError(ex));
    } finally {
      setDeleting(false);
    }
  };

  // ------------------------------------------------------------------
  // Day ↔ Night shift swap (Option A — close + reopen).
  //
  // Josh (Feb 26): "the toggle just moves everything they have open
  // inspections custom jobs etc over to night or to days".
  //
  // Why close+reopen instead of an in-place PATCH:
  //   The prod backend answers `Allow: GET` on `/api/swings/{id}` and
  //   `/api/inspections/{id}` — every mutation attempt on the primary
  //   resource returns 405. So we CAN'T flip the `shift` field on the
  //   current swing directly. What we CAN do is close the swing (via
  //   `POST /swings/{id}/close`) and start a fresh one with the opposite
  //   shift, forwarding the currently-open inspection ids so the crew
  //   sees the same open work under the new shift label.
  //
  // Trade-off (documented to the user before shipping):
  //   Custom jobs (`/asset-demands`) stay attached to the closed swing —
  //   the API doesn't expose a re-parent endpoint, and destroying+recreating
  //   demands would lose their history. Inspections DO follow via the new
  //   swing's `inspection_ids` payload.
  // ------------------------------------------------------------------
  const performShiftSwap = async (opts = {}) => {
    if (!activeSwing?.id) return;
    setSwappingShift(true); setErr("");
    const oldSwingId = activeSwing.id;
    const newShift = shift === "night" ? "day" : "night";
    const now = new Date();
    const dateStr = todayLocalIso(now);
    const wk = weekOfYear(now);
    const newLabel = `Swing · Week ${wk} · ${dateStr} · ${shiftLabel(newShift)}`;
    // Every inspection currently open on the swing — these come with us.
    const carryIds = activeList.map((it) => it.id || it._id).filter(Boolean);
    // Preserve the crew that was on the old swing so we don't lose context.
    const crewId = activeSwing.crew_id || user?.crew_id || user?.assigned_crew_id || undefined;
    const crewName = activeSwing.crew_name || user?.crew || user?.crew_name || user?.assigned_crew_name || undefined;

    try {
      // 0) Detect an OTHER crew already occupying the target shift at this
      //    site. If we haven't been told to "takeover" explicitly, stop and
      //    ask the user to close their swing first — same UX as the manual
      //    Finish Swing button.
      if (!opts.takeover) {
        try {
          const { data } = await api.get("/swings");
          const arr = Array.isArray(data) ? data : (data?.items || []);
          const conflict = arr.find((s) => {
            if (!s || s.id === oldSwingId) return false;
            if (String(s.status || "").toLowerCase() === "closed") return false;
            if (activeSwing.site_code && s.site_code && s.site_code !== activeSwing.site_code) return false;
            return String(s.shift || "").toLowerCase() === newShift;
          });
          if (conflict) {
            setSwappingShift(false);
            setShowShiftSwapConfirm(false);
            setOtherCrewSwing(conflict);
            return;
          }
        } catch {
          // Non-fatal — if the check fails, fall through to the swap and
          // let the backend surface any conflict.
        }
      }

      // 1) If we're taking over from another crew, close their swing first
      //    (same semantics as pressing Finish Swing on their behalf).
      if (opts.takeover && opts.takeoverSwingId) {
        await api.post(`/swings/${opts.takeoverSwingId}/close`, {}).catch(() => null);
      }

      // 2) Close our current swing.
      await api.post(`/swings/${oldSwingId}/close`, {});

      // 3) Start a fresh swing on the opposite shift, carrying the open
      //    inspections across via `inspection_ids`.
      const startBody = {
        client_op_id: `swap-${Date.now()}`,
        swing_label: newLabel,
        start_date: dateStr,
        end_date: dateStr,
        work_area: activeSwing.work_area || "field",
        shift: newShift,
        notes: `Swapped from ${shiftLabel(shift)} shift by ${user?.name || user?.username || "portal"}`,
        inspection_ids: carryIds,
      };
      if (crewId) startBody.crew_id = crewId;
      if (crewName) startBody.crew_name = crewName;

      await api.post("/swings", startBody);

      // 4) Force a resync so the UI immediately reflects the new swing.
      await refreshSwing();
      await reload();
      setShowShiftSwapConfirm(false);
      setOtherCrewSwing(null);
    } catch (ex) {
      setErr(apiError(ex));
    } finally {
      setSwappingShift(false);
    }
  };

  // Nothing open yet? — show the START SWING hero card so the crew explicitly
  // opens their swing (day or night) before adding inspections. Session is
  // now server-backed (`/api/swings`) so every device on the crew sees the
  // same swing.
  if (!activeSwing) {
    if (swingLoading) {
      return (
        <div className="min-h-[calc(100vh-160px)] flex items-center justify-center">
          <div className="flex items-center gap-3 text-muted">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span className="font-display font-black uppercase tracking-widest text-sm">Checking crew swing…</span>
          </div>
        </div>
      );
    }
    return (
      <>
        <StartSwingHero
          suggestedShift={resolvedShift}
          shiftReady={shiftReady}
          dayStart={dayStart}
          dayFinish={dayFinish}
          nightStart={nightStart}
          nightFinish={nightFinish}
          site={user?.site_code}
          crew={user?.crew || user?.assigned_crew_name}
          onStart={(picked) => {
            // Open the inspection picker so the crew can tick assets & work
            // orders. The picker itself calls POST /swings with the required
            // `items` payload — a raw POST with no items is now rejected by
            // the API (422 "items: field required").
            setStartShift(picked);
            setShowInspSwing(true);
          }}
        />
        {showInspSwing && (
          <InspectionSwingModal
            activeSwing={null}
            initialShift={startShift || resolvedShift}
            onClose={() => { setShowInspSwing(false); setStartShift(null); }}
            onCreated={async () => {
              setShowInspSwing(false);
              setStartShift(null);
              await refreshSwing();
              await reload();
            }}
          />
        )}
      </>
    );
  }

  return (
    <div className="space-y-6 relative pb-24" data-testid="swing-page">
      {/* Hero */}
      <section className="panel-heavy p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-5 justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] font-black text-muted flex-wrap">
            <Radio className="w-3.5 h-3.5 animate-pulse" style={{ color: "var(--primary)" }} />
            Live · Week {weekOfYear()} · {shiftLabel(shift)} shift
            {shiftReady && (
              <span className="text-white/50">
                · {shift === "night" ? `${nightStart} → ${nightFinish}` : `${dayStart} → ${dayFinish}`}
              </span>
            )}
            <span>· {user?.site_code || "All sites"}</span>
          </div>
          <h1 className="font-display text-4xl md:text-6xl font-black tracking-tight leading-[1] mt-2">
            SWING
          </h1>
          <p className="text-muted mt-2 text-sm">
            Every inspection you and your crew have running · live progress.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap md:justify-end">
          <button
            className="btn btn-primary"
            style={{ minHeight: 60, fontSize: 17, padding: "0 30px", boxShadow: "0 10px 28px rgba(245,130,32,0.35)" }}
            onClick={() => setShowAction(true)}
            data-testid="swing-action-btn"
            title="Add inspections or custom jobs to your crew's swing"
          >
            <Plus className="w-6 h-6" strokeWidth={2.5} />
            Action
          </button>
        </div>
      </section>

      {/* Slim utility row — supervisor tools tucked below the primary Action
          CTA so the hero stays single-purpose (mobile parity). */}
      <div className="flex items-center gap-2 flex-wrap -mt-2" data-testid="swing-utility-row">
        <button className="btn btn-ghost btn-sm !py-1.5 !text-[11px] uppercase tracking-widest" onClick={reload} disabled={busy} data-testid="swing-refresh">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Refresh
        </button>
        <Link to="/swing-history" className="btn btn-ghost btn-sm !py-1.5 !text-[11px] uppercase tracking-widest" data-testid="swing-open-archive" title="Search past swings and inspections by work order #">
          <History className="w-3.5 h-3.5" /> Archive · WO#
        </Link>
        <Link to={`/shift-summary?date=${todayLocalIso()}&shift=${shift}`} className="btn btn-ghost btn-sm !py-1.5 !text-[11px] uppercase tracking-widest" data-testid="swing-generate-summary" title="Generate the end-of-shift summary for the next crew">
          <ClipboardList className="w-3.5 h-3.5" /> Shift Summary
        </Link>
        {canSupervise && (
          <button
            className="btn btn-sm !py-1.5 !text-[11px] uppercase tracking-widest"
            style={{
              background: shift === "night"
                ? "linear-gradient(180deg, rgba(59,130,246,0.28), rgba(30,64,175,0.28))"
                : "linear-gradient(180deg, rgba(245,130,32,0.28), rgba(217,89,10,0.28))",
              border: `1px solid ${shift === "night" ? "rgba(147,197,253,0.55)" : "rgba(255,180,80,0.55)"}`,
              color: "#fff",
              boxShadow: shift === "night"
                ? "0 4px 12px rgba(59,130,246,0.22)"
                : "0 4px 12px rgba(245,130,32,0.22)",
            }}
            onClick={() => setShowShiftSwapConfirm(true)}
            disabled={swappingShift}
            data-testid="swing-toggle-shift-btn"
            title={`Swap the current swing (${shiftLabel(shift)}) and everything on it over to ${shift === "night" ? "Day" : "Night"} shift`}
          >
            {shift === "night" ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}
            Swap to {shift === "night" ? "Day" : "Night"}
          </button>
        )}
        <div className="flex-1" />
        <button
          className="btn btn-sm !py-1.5 !text-[11px] uppercase tracking-widest"
          style={{
            background: "linear-gradient(180deg, rgba(220,38,38,0.95), rgba(185,28,28,0.95))",
            border: "1px solid rgba(255,120,120,0.5)",
            color: "#fff",
            boxShadow: "0 4px 12px rgba(220,38,38,0.25)",
          }}
          onClick={() => setShowFinishConfirm(true)}
          data-testid="swing-finish-btn"
          title="End the current shift and open the Shift Summary"
        >
          <Flag className="w-3.5 h-3.5" />
          Finish Swing
        </button>
      </div>

      {err && (
        <div className="pill pill-red !text-sm !py-2 !px-3 justify-start">
          <AlertTriangle className="w-3.5 h-3.5" /> {err}
        </div>
      )}

      {/* Active list */}
      <section data-testid="swing-active">
        <div className="flex items-center gap-2 mb-3">
          <PlayCircle className="w-4 h-4" style={{ color: "var(--primary)" }} />
          <div className="text-[11px] uppercase tracking-[0.3em] font-black text-muted">
            In progress <span className="ml-2 text-white/60">· {activeList.length + activeDemands.length}</span>
          </div>
          <div className="flex-1 h-px" style={{ background: "var(--border)" }} />
        </div>
        {inspections === null ? (
          <div className="panel-flat p-10 flex items-center justify-center gap-3 text-muted">
            <Loader2 className="w-5 h-5 animate-spin" /> Loading swing…
          </div>
        ) : (activeList.length === 0 && activeDemands.length === 0) ? (
          <div className="panel-flat p-10 text-center" data-testid="swing-empty">
            <PlayCircle className="w-10 h-10 mx-auto mb-3 text-muted" />
            <div className="font-display font-black text-2xl">No inspections running</div>
            <div className="text-muted text-sm mt-1">Tap <span className="text-white font-bold">Action</span> to add inspections to your crew.</div>
          </div>
        ) : (
          <div className="grid gap-3">
            {/* Custom jobs sit on top so crews can knock them out fast */}
            {activeDemands.map((d) => (
              <AssetDemandRow
                key={`demand-${d.id}`}
                demand={d}
                onOpen={() => setSignoffDemand(d)}
                onDelete={canSupervise ? setDeleteDemand : null}
              />
            ))}
            {activeList.map((it) => (
              <InspectionRow
                key={it.id || it._id}
                item={it}
                onOpen={() => navigate(`/inspections/${it.id || it._id}`)}
                onDelete={canSupervise ? setDeleteTarget : null}
              />
            ))}
          </div>
        )}
      </section>

      {/* Completed list */}
      {(completedList.length > 0 || completedDemands.length > 0) && (
        <section data-testid="swing-completed">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle2 className="w-4 h-4 text-green-400" />
            <div className="text-[11px] uppercase tracking-[0.3em] font-black text-muted">
              Completed this shift <span className="ml-2 text-white/60">· {completedList.length + completedDemands.length}</span>
            </div>
            <div className="flex-1 h-px" style={{ background: "var(--border)" }} />
          </div>
          <div className="grid gap-2">
            {completedDemands.map((d) => (
              <AssetDemandRow
                key={`demand-done-${d.id}`}
                demand={d}
                onOpen={() => setSignoffDemand(d)}
                compact
              />
            ))}
            {completedList.map((it) => (
              <InspectionRow
                key={it.id || it._id}
                item={it}
                onOpen={() => navigate(`/inspections/${it.id || it._id}`)}
                onDelete={canSupervise ? setDeleteTarget : null}
                compact
              />
            ))}
          </div>
        </section>
      )}

      {/* Action menu */}
      {showAction && (
        <ActionMenu
          onClose={() => setShowAction(false)}
          onCustom={() => { setShowAction(false); setShowCustom(true); }}
          onInspSwing={() => { setShowAction(false); setShowInspSwing(true); }}
        />
      )}

      {showInspSwing && (
        <InspectionSwingModal
          activeSwing={activeSwing}
          onClose={() => setShowInspSwing(false)}
          onCreated={() => { setShowInspSwing(false); reload(); }}
        />
      )}

      {showCustom && (
        <CustomJobModal
          onClose={() => setShowCustom(false)}
          onCreated={() => { setShowCustom(false); reload(); }}
        />
      )}

      {showFinishConfirm && (
        <FinishSwingModal
          shift={shift}
          activeCount={activeList.length}
          completedCount={completedList.length}
          onClose={() => setShowFinishConfirm(false)}
          onConfirm={async () => {
            setShowFinishConfirm(false);
            const date = todayLocalIso();
            try { await finishSwing(); } catch (ex) { setErr(apiError(ex)); }
            navigate(`/shift-summary?date=${date}&shift=${shift}&finished=1`);
          }}
        />
      )}

      {showShiftSwapConfirm && (
        <SwapShiftConfirm
          currentShift={shift}
          busy={swappingShift}
          onCancel={() => setShowShiftSwapConfirm(false)}
          onConfirm={() => performShiftSwap()}
        />
      )}

      {otherCrewSwing && (
        <TakeoverShiftConfirm
          currentShift={shift}
          otherSwing={otherCrewSwing}
          busy={swappingShift}
          onCancel={() => setOtherCrewSwing(null)}
          onConfirm={() => performShiftSwap({ takeover: true, takeoverSwingId: otherCrewSwing.id })}
        />
      )}

      {/* Inspection detail now renders on its own /inspections/:id page
          (Josh Feb 23: "seems like it needs a full page to see the inspection") */}

      {deleteTarget && (
        <DeleteInspectionConfirm
          target={deleteTarget}
          busy={deleting}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={confirmDelete}
        />
      )}

      {/* Custom-job sign-off flow */}
      {signoffDemand && (
        <AssetDemandSignoffModal
          demand={signoffDemand}
          onClose={() => setSignoffDemand(null)}
          onSigned={async () => { setSignoffDemand(null); await reload(); }}
        />
      )}

      {/* Delete confirm for a custom job */}
      {deleteDemand && (
        <DeleteDemandConfirm
          demand={deleteDemand}
          busy={deleting}
          onCancel={() => setDeleteDemand(null)}
          onConfirm={confirmDeleteDemand}
        />
      )}
    </div>
  );
}

/* ---------------- Day ↔ Night shift swap confirm ---------------- */
function SwapShiftConfirm({ currentShift, busy, onCancel, onConfirm }) {
  const targetShift = currentShift === "night" ? "day" : "night";
  const targetLabel = currentShift === "night" ? "Day" : "Night";
  const isTargetNight = targetShift === "night";
  return (
    <div
      className="fixed inset-0 z-[65] flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(6px)" }}
      onClick={busy ? undefined : onCancel}
      data-testid="swing-shift-swap-confirm"
    >
      <div
        className="panel-raised w-full max-w-sm p-5 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3">
          <div
            className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
            style={{
              background: isTargetNight ? "rgba(59,130,246,0.18)" : "rgba(245,130,32,0.18)",
              color: isTargetNight ? "#93c5fd" : "#ffb347",
              border: `1px solid ${isTargetNight ? "rgba(147,197,253,0.4)" : "rgba(255,180,80,0.4)"}`,
            }}
          >
            {isTargetNight ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-muted">Swap swing</div>
            <div className="font-display font-black text-xl leading-tight mt-0.5">
              Swap to {targetLabel}?
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2" style={{ borderTop: "1px solid var(--border)" }}>
          <button
            className="btn btn-ghost btn-sm"
            onClick={onCancel}
            disabled={busy}
            data-testid="swing-shift-swap-cancel"
          >
            Cancel
          </button>
          <button
            className="btn btn-sm btn-primary"
            onClick={onConfirm}
            disabled={busy}
            data-testid="swing-shift-swap-confirm-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (isTargetNight ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />)}
            Swap to {targetLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Another-crew-already-on-target-shift takeover ---------------- */
function TakeoverShiftConfirm({ currentShift, otherSwing, busy, onCancel, onConfirm }) {
  const targetShift = currentShift === "night" ? "day" : "night";
  const targetLabel = currentShift === "night" ? "Day" : "Night";
  const isTargetNight = targetShift === "night";
  const otherCrewName = otherSwing?.crew_name || "another crew";
  const openedAt = otherSwing?.created_at
    ? new Date(otherSwing.created_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })
    : null;
  return (
    <div
      className="fixed inset-0 z-[66] flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.8)", backdropFilter: "blur(6px)" }}
      onClick={busy ? undefined : onCancel}
      data-testid="swing-takeover-confirm"
    >
      <div
        className="panel-raised w-full max-w-md p-5 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3">
          <div
            className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
            style={{
              background: "rgba(245,130,32,0.18)",
              color: "#ffb347",
              border: "1px solid rgba(255,180,80,0.4)",
            }}
          >
            <Users className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-muted">Another crew is already on {targetLabel}</div>
            <div className="font-display font-black text-xl leading-tight mt-1">
              Take over from <span style={{ color: isTargetNight ? "#93c5fd" : "#ffb347" }}>{otherCrewName}</span>?
            </div>
            <div className="text-sm text-white/80 mt-3 leading-relaxed space-y-2">
              <div>
                <span className="text-white font-bold">{otherCrewName}</span> currently has the {targetLabel} shift open{openedAt ? <> since <span className="text-white/90 font-mono-tight">{openedAt}</span></> : null}.
              </div>
              <div>
                Confirming will <span className="text-white font-bold">finish their swing</span> (same as pressing Finish Swing on their behalf) and then swap you across to {targetLabel} with your open inspections carrying over.
              </div>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2" style={{ borderTop: "1px solid var(--border)" }}>
          <button
            className="btn btn-ghost btn-sm"
            onClick={onCancel}
            disabled={busy}
            data-testid="swing-takeover-cancel"
          >
            Cancel
          </button>
          <button
            className="btn btn-sm btn-primary"
            onClick={onConfirm}
            disabled={busy}
            data-testid="swing-takeover-confirm-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Users className="w-4 h-4" />}
            Finish their swing &amp; swap
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Delete custom-job confirm ---------------- */
function DeleteDemandConfirm({ demand, busy, onCancel, onConfirm }) {
  return (
    <div
      className="fixed inset-0 z-[65] flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(6px)" }}
      onClick={onCancel}
      data-testid="custom-job-delete-confirm"
    >
      <div
        className="panel-raised w-full max-w-md p-5 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2">
          <Trash2 className="w-4 h-4 text-red-400" />
          <div className="font-display text-lg font-black uppercase tracking-widest">Delete custom job?</div>
        </div>
        <div className="text-sm text-white/80 line-clamp-3">
          {demand?.text || "—"}
        </div>
        <div className="text-xs text-muted">
          Asset: <span className="text-white font-mono-tight">{demand?.asset_id}</span>
        </div>
        <div className="flex items-center justify-end gap-2 pt-2">
          <button className="btn btn-ghost btn-sm" onClick={onCancel} disabled={busy} data-testid="custom-job-delete-cancel">
            Cancel
          </button>
          <button
            className="btn btn-sm"
            style={{ background: "rgba(220,38,38,0.9)", border: "1px solid rgba(255,120,120,0.5)", color: "#fff" }}
            onClick={onConfirm}
            disabled={busy}
            data-testid="custom-job-delete-confirm-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Delete confirm ---------------- */
function DeleteInspectionConfirm({ target, busy, onCancel, onConfirm }) {
  return (
    <div
      className="fixed inset-0 z-[65] flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(6px)" }}
      onClick={onCancel}
      data-testid="swing-delete-confirm"
    >
      <div
        className="panel-raised w-full max-w-md p-5 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3">
          <div
            className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
            style={{ background: "rgba(220,38,38,0.18)", color: "#ff9b9b", border: "1px solid rgba(255,120,120,0.35)" }}
          >
            <Trash2 className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-muted">Delete inspection</div>
            <div className="font-display font-black text-xl leading-tight mt-1 break-words">
              Delete inspection for {target.asset_id || "this asset"}?
            </div>
            {target.template_name && (
              <div className="text-sm text-muted mt-1 truncate">{target.template_name}</div>
            )}
            <div className="text-sm text-white/80 mt-3 leading-relaxed">
              This removes the inspection and every response your crew has recorded on it. It disappears from the mobile app within 15 seconds. This cannot be undone.
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2" style={{ borderTop: "1px solid var(--border)" }}>
          <button
            className="btn btn-ghost btn-sm"
            onClick={onCancel}
            disabled={busy}
            data-testid="swing-delete-cancel"
          >
            Cancel
          </button>
          <button
            className="btn btn-sm"
            onClick={onConfirm}
            disabled={busy}
            data-testid="swing-delete-confirm-btn"
            style={{
              background: "linear-gradient(180deg, rgba(220,38,38,0.95), rgba(185,28,28,0.95))",
              border: "1px solid rgba(255,120,120,0.5)",
              color: "#fff",
              boxShadow: "0 6px 18px rgba(220,38,38,0.35)",
            }}
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            Delete inspection
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Row ---------------- */
function InspectionRow({ item, onOpen, onDelete, compact }) {
  const p = progressOf(item);
  const t = TONE[p.tone];
  const tech = item.inspector_name || item.created_by_name || item.assignee_name || item.tradesman;
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen?.(); } }}
      className={`panel p-4 md:p-5 text-left group hover:border-orange-500/40 transition-all cursor-pointer ${compact ? "" : "md:p-5"}`}
      data-testid={`swing-row-${item.id || item._id}`}
    >
      <div className="flex items-center gap-4">
        <div className="w-11 h-11 shrink-0 rounded-xl flex items-center justify-center"
             style={{ background: t.bg, border: `1px solid ${t.border}`, color: t.color }}>
          {p.icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-display font-black text-base md:text-lg tracking-tight truncate">
              {item.asset_id || item.asset_name || "Asset"}
            </div>
            <span className="pill !text-[10px] !py-0.5 !px-2"
                  style={{ background: t.bg, border: `1px solid ${t.border}`, color: t.color }}>
              {p.label}
            </span>
            {tech && (
              <span className="pill !text-[10px] !py-0.5 !px-2" title="Technician on this inspection">
                <Users className="w-3 h-3" /> {tech}
              </span>
            )}
          </div>
          <div className="text-[11px] font-mono-tight text-muted mt-0.5 truncate">
            {item.template_name || item.title || "Inspection"}
            {item.updated_at && <> · Last update {fmtRel(item.updated_at)} · {fmtTime(item.updated_at)}</>}
          </div>
          {/* Progress bar */}
          <div className="h-1.5 rounded-full mt-3 overflow-hidden"
               style={{ background: "rgba(255,255,255,0.06)", border: "1px solid var(--border)" }}>
            <div className="h-full transition-all duration-500"
                 style={{ width: `${p.pct}%`, background: t.color }} />
          </div>
        </div>
        <div className="text-right shrink-0 pl-2">
          <div className="font-display font-black text-3xl md:text-4xl tabular-nums leading-none" style={{ color: t.color }}>
            {p.pct}<span className="text-lg text-muted">%</span>
          </div>
        </div>
        {onDelete && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onDelete(item); }}
            className="w-9 h-9 shrink-0 flex items-center justify-center rounded-lg transition-colors ml-1"
            style={{
              color: "#ff9b9b",
              background: "rgba(220, 38, 38, 0.06)",
              border: "1px solid rgba(220, 38, 38, 0.2)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(220, 38, 38, 0.18)";
              e.currentTarget.style.borderColor = "rgba(255, 120, 120, 0.6)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "rgba(220, 38, 38, 0.06)";
              e.currentTarget.style.borderColor = "rgba(220, 38, 38, 0.2)";
            }}
            title="Delete inspection"
            aria-label={`Delete inspection for ${item.asset_id || "asset"}`}
            data-testid={`swing-row-delete-${item.id || item._id}`}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
        <ChevronRight className="w-5 h-5 text-muted group-hover:text-white transition-colors" />
      </div>
    </div>
  );
}

/* ---------------- Action menu ---------------- */
function ActionMenu({ onClose, onCustom, onInspSwing }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="action-menu">
      <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.72)", backdropFilter: "blur(6px)" }} />
      <div className="relative panel-heavy w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 mb-5">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center"
               style={{ background: "var(--primary)", color: "#0a0a0a" }}>
            <Plus className="w-5 h-5" strokeWidth={3} />
          </div>
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-widest text-muted">Add to swing</div>
            <div className="font-display font-black text-xl tracking-tight">What are we doing?</div>
          </div>
          <button className="btn btn-sm btn-ghost !px-2" onClick={onClose}><XIcon className="w-4 h-4" /></button>
        </div>
        <div className="grid gap-3">
          <ActionTile
            icon={ClipboardList}
            label="Start Batch Inspection"
            desc="Pick a machine · add another · another · assign the batch to your crew."
            onClick={onInspSwing}
            testid="action-inspection-swing"
            primary
          />
          <ActionTile
            icon={Layers}
            label="Add Custom Job"
            desc="Bucket sheet, GET wear or any site-specific form."
            onClick={onCustom}
            testid="action-custom-job"
          />
        </div>
      </div>
    </div>
  );
}

function ActionTile({ icon: Icon, label, desc, onClick, testid, primary }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-left p-5 rounded-xl transition-transform hover:translate-y-[-2px] ${primary ? "" : ""}`}
      style={primary
        ? { background: "var(--primary)", color: "#0a0a0a" }
        : { background: "var(--surface-2)", border: "1px solid var(--border)" }}
      data-testid={testid}
    >
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
             style={primary
               ? { background: "#0a0a0a", color: "var(--primary)" }
               : { background: "rgba(245,130,32,0.15)", color: "var(--primary)", border: "1px solid rgba(245,130,32,0.3)" }}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-black text-lg tracking-tight">{label}</div>
          <div className={`text-xs mt-0.5 ${primary ? "text-black/70" : "text-muted"}`}>{desc}</div>
        </div>
        <ChevronRight className="w-5 h-5 opacity-70" />
      </div>
    </button>
  );
}

/* ---------------- Start Swing hero (day / night picker) ---------------- */
function StartSwingHero({ suggestedShift, shiftReady, dayStart, dayFinish, nightStart, nightFinish, site, crew, onStart }) {
  const now = new Date();
  const wk = weekOfYear();
  const timeLabel = now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  const dateLabel = now.toLocaleDateString([], { weekday: "long", day: "2-digit", month: "long" });
  return (
    <div className="min-h-[calc(100vh-160px)] flex items-center justify-center px-4 relative" data-testid="start-swing-hero">
      {/* Ambient glow */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: "radial-gradient(circle at 50% 30%, rgba(245,130,32,0.14) 0%, transparent 55%)",
        }}
      />

      <div className="relative panel-heavy w-full max-w-2xl p-8 md:p-10">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] font-black text-muted flex-wrap">
          <Radio className="w-3.5 h-3.5 animate-pulse" style={{ color: "var(--primary)" }} />
          Week {wk} · {dateLabel}
          <span className="text-white/50 tabular-nums">· {timeLabel}</span>
          {site && <span className="text-white/50">· {site}</span>}
        </div>

        <h1 className="font-display text-5xl md:text-7xl font-black tracking-tight leading-[0.95] mt-3">
          READY TO<br />START SWING?
        </h1>
        <p className="text-muted mt-3 text-sm">
          Choose Day or Night to open your swing. You&apos;ll add inspections, custom jobs and handovers straight onto it, then tap
          <span className="text-white font-bold"> Finish Swing</span> at the end — it archives automatically for the next crew and Swing History.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6">
          <ShiftCard
            label="DAY SWING"
            icon={<Sun className="w-6 h-6" />}
            window={shiftReady ? `${dayStart} → ${dayFinish}` : "Day hours"}
            highlight={suggestedShift === "day"}
            color="#f58220"
            testid="start-day-swing"
            onClick={() => onStart("day")}
          />
          <ShiftCard
            label="NIGHT SWING"
            icon={<Moon className="w-6 h-6" />}
            window={shiftReady ? `${nightStart} → ${nightFinish}` : "Night hours"}
            highlight={suggestedShift === "night"}
            color="#93c5fd"
            testid="start-night-swing"
            onClick={() => onStart("night")}
          />
        </div>

        <div className="mt-6 flex items-center gap-3 text-xs text-white/70 flex-wrap">
          {crew && (
            <span className="pill !text-[10px] !py-1 !px-2.5">
              <Users className="w-3 h-3" /> {crew}
            </span>
          )}
          <span className="pill !text-[10px] !py-1 !px-2.5" title="Auto-suggested from site config">
            <Radio className="w-3 h-3" style={{ color: "var(--primary)" }} />
            Suggested: {suggestedShift === "night" ? "NIGHT" : "DAY"}
          </span>
          <Link to="/swing-history" className="pill !text-[10px] !py-1 !px-2.5 hover:!opacity-100" style={{ opacity: 0.85 }} data-testid="link-swing-history">
            <History className="w-3 h-3" /> View past swings
          </Link>
        </div>
      </div>
    </div>
  );
}

function ShiftCard({ label, icon, window: win, highlight, color, testid, onClick }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="text-left panel-flat p-5 relative overflow-hidden transition-transform hover:-translate-y-0.5"
      style={{
        border: highlight ? `2px solid ${color}` : "1px solid var(--border)",
        boxShadow: highlight ? `0 12px 32px ${hexToRgba(color, 0.28)}, inset 0 0 0 1px ${hexToRgba(color, 0.35)}` : "none",
      }}
    >
      {highlight && (
        <div
          className="absolute top-2 right-2 pill !text-[9px] !py-0.5 !px-2 font-black uppercase tracking-widest"
          style={{ background: hexToRgba(color, 0.15), border: `1px solid ${color}`, color }}
        >
          SUGGESTED NOW
        </div>
      )}
      <div className="flex items-center gap-3">
        <div
          className="w-11 h-11 rounded-xl flex items-center justify-center"
          style={{
            background: hexToRgba(color, 0.15),
            border: `1px solid ${hexToRgba(color, 0.35)}`,
            color,
          }}
        >
          {icon}
        </div>
        <div>
          <div className="font-display font-black text-lg tracking-tight" style={{ color }}>{label}</div>
          <div className="text-[11px] uppercase tracking-widest text-muted mt-0.5">{win}</div>
        </div>
      </div>
      <div className="mt-4 pill !text-xs !py-1.5 !px-3 font-black"
           style={{ background: hexToRgba(color, 0.15), color, border: `1px solid ${color}` }}>
        <PlayCircle className="w-3.5 h-3.5" /> Start
      </div>
    </button>
  );
}

function hexToRgba(hex, alpha) {
  const h = hex.replace("#", "");
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

/* ---------------- Finish Swing confirmation ---------------- */
function FinishSwingModal({ shift, activeCount, completedCount, onClose, onConfirm }) {
  const label = shift === "night" ? "Night" : "Day";
  const hasActive = activeCount > 0;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="finish-swing-modal"
    >
      <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.78)", backdropFilter: "blur(6px)" }} />
      <div className="relative panel-heavy w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 mb-5">
          <div
            className="w-11 h-11 rounded-xl flex items-center justify-center"
            style={{
              background: "linear-gradient(180deg, rgba(220,38,38,0.95), rgba(185,28,28,0.95))",
              color: "#fff",
              boxShadow: "0 6px 18px rgba(220,38,38,0.35)",
            }}
          >
            <Flag className="w-5 h-5" strokeWidth={2.5} />
          </div>
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-widest text-muted">End of shift</div>
            <div className="font-display font-black text-xl tracking-tight">Finish {label} Swing?</div>
          </div>
          <button className="btn btn-sm btn-ghost !px-2" onClick={onClose} data-testid="finish-swing-close">
            <XIcon className="w-4 h-4" />
          </button>
        </div>

        <div className="text-sm text-white/80 leading-relaxed">
          This will wrap up the {label.toLowerCase()} swing and open the{" "}
          <span className="font-bold text-white">Shift Summary</span> for handover to the next crew.
        </div>

        <div className="grid grid-cols-2 gap-2 mt-4">
          <div
            className="rounded-xl p-3 border"
            style={{ background: "rgba(245,130,32,0.06)", borderColor: "rgba(245,130,32,0.3)" }}
          >
            <div className="text-[10px] uppercase tracking-widest text-muted">In progress</div>
            <div className="font-display font-black text-2xl tabular-nums" style={{ color: "var(--primary)" }}>
              {activeCount}
            </div>
          </div>
          <div
            className="rounded-xl p-3 border"
            style={{ background: "rgba(74,222,128,0.06)", borderColor: "rgba(74,222,128,0.3)" }}
          >
            <div className="text-[10px] uppercase tracking-widest text-muted">Completed</div>
            <div className="font-display font-black text-2xl tabular-nums text-green-400">{completedCount}</div>
          </div>
        </div>

        {hasActive && (
          <div
            className="mt-4 flex items-start gap-2 rounded-lg p-3 text-xs"
            style={{ background: "rgba(255,155,80,0.08)", border: "1px solid rgba(255,155,80,0.35)" }}
            data-testid="finish-swing-warning"
          >
            <AlertTriangle className="w-4 h-4 shrink-0 mt-[1px]" style={{ color: "#ffb347" }} />
            <div className="text-white/85">
              {activeCount} inspection{activeCount === 1 ? "" : "s"} still in progress. They&apos;ll be listed as
              open on the summary — the incoming crew can carry them over.
            </div>
          </div>
        )}

        <div className="flex items-center gap-2 justify-end mt-6">
          <button className="btn btn-ghost btn-sm" onClick={onClose} data-testid="finish-swing-cancel">
            Cancel
          </button>
          <button
            className="btn btn-sm"
            style={{
              background: "linear-gradient(180deg, rgba(220,38,38,0.95), rgba(185,28,28,0.95))",
              border: "1px solid rgba(255,120,120,0.5)",
              color: "#fff",
              boxShadow: "0 6px 18px rgba(220,38,38,0.35)",
              minHeight: 42,
              padding: "0 18px",
              fontSize: 14,
            }}
            onClick={onConfirm}
            data-testid="finish-swing-confirm"
          >
            <Flag className="w-4 h-4" />
            Finish &amp; Open Summary
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Inspection swing modal ---------------- */
function InspectionSwingModal({ activeSwing, initialShift, onClose, onCreated }) {
  const { user } = useAuth();
  const { shift: resolvedShift } = useSiteShift();
  const [assets, setAssets] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [crews, setCrews] = useState([]);
  // "Currently editing" template — the one whose machines are visible in the
  // grid below. Users can switch between templates freely; their previously
  // ticked machines are preserved in `basket` so nothing is lost.
  const [templateId, setTemplateId] = useState("");
  // Basket — a running total across MULTIPLE inspection types. Shape:
  //   { [templateId]: Set<assetId> }
  // Adding Excavators, then Dozers, then Drills all accumulate here; the
  // "Start Swing" button fires ONE bulk create at the end. This is what
  // supervisors actually asked for — one swing action can now contain
  // Excavator + Dozer + Drill inspections in a single go.
  const [basket, setBasket] = useState({});
  // Per-asset optional Work Order # — keyed by asset id. Empty string / not
  // present ⇒ "skipped, add later". Populated ⇒ posted with the new
  // inspection so the WO# lands on the compliance PDF from day 1.
  const [woByAsset, setWoByAsset] = useState({});
  const [crewId, setCrewId] = useState("");
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [loadingRefs, setLoadingRefs] = useState(true);
  const [shiftType, setShiftType] = useState(() => {
    const pick = String(initialShift || resolvedShift || "day").toLowerCase();
    return pick === "night" ? "night" : "day";
  });
  const weekNo = weekOfYear(new Date());

  // If the site-config resolves after mount, sync the toggle default once —
  // but only if the caller didn't preset the shift (e.g. from the Start
  // Swing hero picking Day/Night).
  useEffect(() => {
    if (initialShift) return;
    if (resolvedShift) setShiftType(resolvedShift);
  }, [resolvedShift, initialShift]);

  useEffect(() => {
    (async () => {
      setLoadingRefs(true);
      const [a, t, c, myCrew] = await Promise.allSettled([
        api.get("/assets"),
        api.get("/inspection-templates"),
        api.get("/crews"),
        api.get("/users/my-crew").catch(() => ({ data: null })),
      ]);
      const list = (r) => {
        if (r.status !== "fulfilled") return [];
        const d = r.value.data;
        return Array.isArray(d) ? d : d?.items || d?.results || d?.data || [];
      };
      setAssets(list(a));
      setTemplates(list(t));
      setCrews(list(c));
      // default crew: user's own crew if we can infer it
      const mine = myCrew.status === "fulfilled" ? myCrew.value.data : null;
      const myCrewId = mine?.crew_id || mine?.id || user?.crew_id;
      if (myCrewId) setCrewId(String(myCrewId));
      setLoadingRefs(false);
    })().catch(() => setLoadingRefs(false));
  }, [user?.crew_id]);

  const currentTemplate = templates.find((t) => String(t.id || t._id) === String(templateId));
  const templateMachine = norm(currentTemplate?.machine_type || currentTemplate?.applies_to || "");

  // Ticked machines for the currently visible template (drives the grid).
  const currentSelected = useMemo(() => basket[templateId] || new Set(), [basket, templateId]);

  // Total across every template in the basket — powers the sticky footer
  // count and the Start Swing / Add to Crew button label.
  const totalMachines = useMemo(
    () => Object.values(basket).reduce((n, s) => n + (s?.size || 0), 0),
    [basket]
  );

  // Sorted list of populated basket entries — used for the "basket summary"
  // strip at the top of the modal and for building the final POST payload.
  const basketEntries = useMemo(() => {
    return Object.entries(basket)
      .filter(([, s]) => s && s.size > 0)
      .map(([tid, s]) => {
        const t = templates.find((x) => String(x.id || x._id) === String(tid));
        return {
          templateId: tid,
          templateName: (t?.name || t?.title || "Inspection"),
          templateMachine: t?.machine_type || t?.applies_to || "",
          assetIds: Array.from(s),
        };
      });
  }, [basket, templates]);

  const filteredAssets = useMemo(() => {
    const q = query.trim().toLowerCase();
    let base = assets.filter((a) => !a.retired);
    if (templateMachine) {
      base = base.filter((a) => {
        const at = norm(a.asset_type || a.category || a.machine_model || "");
        return at.includes(templateMachine) || templateMachine.includes(at);
      });
    }
    if (!q) return base;
    return base.filter((a) => {
      const hay = [a.id, a.name, a.machine_model, a.asset_type, a.bucket_number]
        .filter(Boolean).map((x) => String(x).toLowerCase()).join(" ");
      return hay.includes(q);
    });
  }, [assets, query, templateMachine]);

  const toggleAsset = (id) => {
    if (!templateId) return;
    setBasket((prev) => {
      const next = { ...prev };
      const s = new Set(next[templateId] || []);
      if (s.has(id)) s.delete(id); else s.add(id);
      if (s.size === 0) delete next[templateId];
      else next[templateId] = s;
      return next;
    });
  };
  const selectAll = () => {
    if (!templateId) return;
    setBasket((prev) => ({
      ...prev,
      [templateId]: new Set(filteredAssets.map((a) => String(a.id || a._id))),
    }));
  };
  const clearAll = () => {
    if (!templateId) return;
    setBasket((prev) => { const n = { ...prev }; delete n[templateId]; return n; });
  };
  // Remove one template's picks from the basket entirely — used by the ×
  // button on each basket-summary chip.
  const removeFromBasket = (tid) => {
    setBasket((prev) => { const n = { ...prev }; delete n[tid]; return n; });
  };

  // Helper — patch the WO# onto every newly-created inspection whose asset
  // had a WO# filled in. Called after the primary bulk / individual create
  // succeeds. Non-blocking: silent failures are logged only.
  const applyWorkOrders = async () => {
    const woMap = woByAsset || {};
    const allBasketAssets = basketEntries.flatMap((b) => b.assetIds);
    const withWo = allBasketAssets.filter((aid) => (woMap[aid] || "").trim());
    if (withWo.length === 0) return;
    try {
      const list = await api.get("/inspections", { params: { site_code: user?.site_code || undefined } });
      const arr = Array.isArray(list.data) ? list.data : (list.data?.items || list.data?.results || []);
      // Latest planned/draft per asset in this shift's window.
      const byAsset = new Map();
      arr.forEach((it) => {
        if (!withWo.includes(it.asset_id)) return;
        const prev = byAsset.get(it.asset_id);
        const ts = new Date(it.created_at || it.updated_at || 0).getTime();
        if (!prev || ts > prev._ts) byAsset.set(it.asset_id, { ...it, _ts: ts });
      });
      await Promise.allSettled(withWo.map((aid) => {
        const insp = byAsset.get(aid);
        const wo = String(woMap[aid] || "").trim();
        if (!insp || !wo) return Promise.resolve();
        return api.patch(`/inspections/${insp.id || insp._id}`, { work_order_number: wo });
      }));
    } catch (ex) {
      // Non-critical — the user can still add WO# on the inspection detail.
      console.warn("[wo-apply] failed:", ex?.message);
    }
  };

  const submit = async () => {
    if (basketEntries.length === 0) {
      setMsg({ type: "err", text: "Add at least one machine to the basket. Pick a template, tick some machines, then Start Swing." });
      return;
    }
    setBusy(true); setMsg(null);
    const now = new Date();
    const today = todayLocalIso(now);
    const endDate = todayLocalIso(new Date(now.getTime() + 12 * 3600 * 1000));
    // Flatten every (template, asset) pair from the basket into a single
    // items[] array. Order is preserved so the first-picked template appears
    // first on the shift board.
    const items = basketEntries.flatMap((b) =>
      b.assetIds.map((asset_id) => ({
        asset_id,
        template_id: b.templateId,
        // Include WO# in the body up-front so servers that accept it on create
        // (verified against prod) store it immediately. Servers that ignore it
        // are patched afterwards by applyWorkOrders().
        work_order_number: (woByAsset[asset_id] || "").trim() || undefined,
      }))
    );

    // If the crew already has an active swing running, attach the new
    // inspections to THAT swing instead of creating a new one — mirrors the
    // mobile app's "everything on the current swing" contract.
    if (activeSwing?.id) {
      try {
        // Best-guess primary path: POST items to the swing's inspection
        // collection endpoint.
        await api.post(`/swings/${activeSwing.id}/inspections`, { items });
        await applyWorkOrders();
        setMsg({ type: "ok", text: `Added ${items.length} inspection${items.length === 1 ? "" : "s"} to the current swing.` });
        setTimeout(onCreated, 700);
        return;
      } catch (ex1) {
        // Fallback: create each inspection individually and stamp the
        // active swing_id so the server-side link is established.
        try {
          const res = await Promise.allSettled(items.map((it) =>
            api.post("/inspections", {
              asset_id: it.asset_id,
              template_id: it.template_id,
              swing_id: activeSwing.id,
              crew_id: activeSwing.crew_id || crewId || undefined,
              shift: shiftType,
              site_code: user?.site_code || undefined,
              work_order_number: it.work_order_number,
            }),
          ));
          const ok = res.filter((r) => r.status === "fulfilled").length;
          const fail = res.length - ok;
          await applyWorkOrders();
          setBusy(false);
          setMsg(fail === 0
            ? { type: "ok", text: `Added ${ok} inspection${ok === 1 ? "" : "s"} to the current swing.` }
            : { type: "err", text: `${ok} added, ${fail} failed. ${apiError(res.find(r => r.status === "rejected")?.reason)}` });
          if (ok > 0) setTimeout(onCreated, 700);
          return;
        } catch (ex2) {
          setBusy(false);
          setMsg({ type: "err", text: apiError(ex2) || apiError(ex1) });
          return;
        }
      }
    }

    // No active swing yet — create one via the existing bulk endpoint.
    try {
      const body = {
        items,
        crew_id: crewId || user?.crew_id || undefined,
        swing_label: `Swing · Week ${weekNo} · ${today} · ${shiftLabel(shiftType)}`,
        start_date: today,
        end_date: endDate,
        week_of_year: weekNo,
        site_code: user?.site_code || undefined,
        work_area: user?.work_area || user?.site_code || "",
        supervisor_id: isSupervisor(user) ? (user?.id || user?._id) : undefined,
        supervisor_name: isSupervisor(user) ? (user?.name || user?.username) : undefined,
        trade: user?.trade || undefined,
        shift: shiftType,
        notes: `Started by ${user?.name || user?.username || "supervisor"}`,
        client_op_id: `swing-${now.getTime()}`,
      };
      await api.post("/swings", body);
      await applyWorkOrders();
      setMsg({ type: "ok", text: `Added ${items.length} inspection${items.length === 1 ? "" : "s"} to ${crewId ? "your crew" : "the swing"}.` });
      setTimeout(onCreated, 700);
    } catch (ex) {
      // Fallback for older backends — POST each (template, asset) pair
      // one at a time, iterating the same items[] we already built.
      if (ex?.response?.status === 404) {
        const res = await Promise.allSettled(
          items.map((it) => api.post("/inspections", {
            asset_id: it.asset_id,
            template_id: it.template_id,
            crew_id: crewId || undefined,
            shift: shiftType,
            work_order_number: it.work_order_number,
          }))
        );
        const ok = res.filter((r) => r.status === "fulfilled").length;
        const fail = res.length - ok;
        await applyWorkOrders();
        setBusy(false);
        setMsg(fail === 0
          ? { type: "ok", text: `Added ${ok} inspection${ok === 1 ? "" : "s"}.` }
          : { type: "err", text: `${ok} added, ${fail} failed. ${apiError(res.find(r => r.status === "rejected")?.reason)}` });
        if (ok > 0) setTimeout(onCreated, 700);
      } else {
        setBusy(false);
        setMsg({ type: "err", text: apiError(ex) });
      }
      return;
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="insp-swing-modal">
      <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.72)", backdropFilter: "blur(6px)" }} onClick={onClose} />
      <div className="relative panel-heavy w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="flex items-center gap-3 p-5" style={{ borderBottom: "1px solid var(--border)" }}>
          <div className="w-10 h-10 rounded-xl flex items-center justify-center"
               style={{ background: "rgba(245,130,32,0.15)", color: "var(--primary)", border: "1px solid rgba(245,130,32,0.3)" }}>
            <ClipboardList className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-widest text-muted">{activeSwing ? "Add inspections to your crew" : "Start swing"}</div>
            <div className="font-display font-black text-xl tracking-tight">Pick as many types as you need · Excavator + Dozer + Drill…</div>
          </div>
          <button className="btn btn-sm btn-ghost !px-2" onClick={onClose} data-testid="insp-swing-close"><XIcon className="w-4 h-4" /></button>
        </div>

        <div className="p-5 overflow-y-auto flex-1 space-y-4">
          {loadingRefs && (
            <div className="pill !text-xs !py-1.5 !px-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading templates & assets…</div>
          )}

          {/* Shift & Week banner — mirrors mobile app */}
          <div
            className="panel-flat p-4 flex flex-col sm:flex-row sm:items-center gap-3"
            data-testid="insp-swing-shift-banner"
          >
            <div className="text-center px-4 py-2 rounded-xl shrink-0"
                 style={{ background: "rgba(245,130,32,0.12)", border: "1px solid rgba(245,130,32,0.3)", color: "var(--primary)" }}>
              <div className="text-[10px] uppercase tracking-widest font-black opacity-80">Week</div>
              <div className="font-display font-black text-2xl leading-none tabular-nums" data-testid="insp-swing-week">
                {weekNo}
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-widest font-black text-muted">Shift Type</div>
              <div className="flex gap-2 mt-1.5" data-testid="insp-swing-shift-toggle">
                <button
                  type="button"
                  onClick={() => setShiftType("day")}
                  className={`flex-1 py-2.5 rounded-xl font-display font-black uppercase tracking-widest text-sm transition-all ${
                    shiftType === "day" ? "text-black scale-105 shadow-lg shadow-orange-500/30" : "text-white/80 hover:text-white"
                  }`}
                  style={shiftType === "day"
                    ? { background: "var(--primary)" }
                    : { background: "var(--surface-2)", border: "1px solid var(--border)" }}
                  data-testid="insp-swing-shift-day"
                >
                  ☀ Day
                </button>
                <button
                  type="button"
                  onClick={() => setShiftType("night")}
                  className={`flex-1 py-2.5 rounded-xl font-display font-black uppercase tracking-widest text-sm transition-all ${
                    shiftType === "night" ? "text-black scale-105 shadow-lg shadow-orange-500/30" : "text-white/80 hover:text-white"
                  }`}
                  style={shiftType === "night"
                    ? { background: "var(--primary)" }
                    : { background: "var(--surface-2)", border: "1px solid var(--border)" }}
                  data-testid="insp-swing-shift-night"
                >
                  ☾ Night
                </button>
              </div>
            </div>
            <div className="text-right text-xs font-mono-tight text-muted shrink-0">
              <div>{user?.site_code || "—"}</div>
              <div>{new Date().toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}</div>
            </div>
          </div>

          {/* Profile pre-fill hint — matches mobile app behaviour */}
          <div
            className="panel-flat p-3 flex items-center gap-3 flex-wrap"
            data-testid="insp-swing-profile-hint"
            title="These come from your profile automatically"
          >
            <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                 style={{ background: "rgba(245,130,32,0.15)", color: "var(--primary)", border: "1px solid rgba(245,130,32,0.3)" }}>
              <Users className="w-4 h-4" />
            </div>
            <div className="text-[10px] uppercase tracking-widest text-muted">Auto-filled from your profile</div>
            {user?.name && <span className="pill !text-[10px] !py-0.5 !px-2" data-testid="pfhint-name">{user.name}</span>}
            {user?.role && <span className="pill pill-orange !text-[10px] !py-0.5 !px-2" data-testid="pfhint-role">{user.role}</span>}
            {user?.site_code && <span className="pill !text-[10px] !py-0.5 !px-2" data-testid="pfhint-site">Site · {user.site_code}</span>}
            {user?.work_area && <span className="pill !text-[10px] !py-0.5 !px-2" data-testid="pfhint-work-area">Area · {user.work_area}</span>}
            {user?.crew_name && <span className="pill !text-[10px] !py-0.5 !px-2" data-testid="pfhint-crew">Crew · {user.crew_name}</span>}
            {user?.trade && <span className="pill !text-[10px] !py-0.5 !px-2" data-testid="pfhint-trade">{user.trade}</span>}
          </div>

          {/* Basket summary — running total across every inspection type
              the supervisor has ticked so far. This is what makes it OBVIOUS
              that Excavator + Dozer + Drill can all live on the same swing:
              each type shows up as its own chip with a × to remove. Empty
              basket = shows a big "Add your first inspection type below"
              coach mark so users know they can stack. */}
          <div className="panel-flat p-4" data-testid="insp-swing-basket">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-widest font-black text-muted">
                Your Swing Basket
              </div>
              <div className="text-[10px] uppercase tracking-widest text-muted tabular-nums">
                {totalMachines} inspection{totalMachines === 1 ? "" : "s"} · {basketEntries.length} type{basketEntries.length === 1 ? "" : "s"}
              </div>
            </div>
            {basketEntries.length === 0 ? (
              <div className="flex items-center gap-3 py-2">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                     style={{ background: "rgba(255,255,255,0.05)", border: "1px dashed rgba(255,255,255,0.2)", color: "var(--muted)" }}>
                  <PlusCircle className="w-5 h-5" />
                </div>
                <div className="text-sm text-muted">
                  <div className="font-display font-black text-white text-[13px] uppercase tracking-widest">Add your first inspection type below</div>
                  <div className="text-[11px] mt-0.5">Pick a template · tick machines · <span className="text-white font-bold">then pick another template</span> to stack more (Excavator + Dozer + Drill etc.)</div>
                </div>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2" data-testid="insp-swing-basket-chips">
                {basketEntries.map((b) => (
                  <button
                    key={b.templateId}
                    type="button"
                    onClick={() => setTemplateId(b.templateId)}
                    className="pill !text-[11px] !py-1 !px-2 flex items-center gap-1.5 hover:opacity-90"
                    style={{
                      background: String(b.templateId) === String(templateId) ? "rgba(245,130,32,0.2)" : "rgba(255,255,255,0.05)",
                      border: `1px solid ${String(b.templateId) === String(templateId) ? "var(--primary)" : "rgba(255,255,255,0.15)"}`,
                      color: String(b.templateId) === String(templateId) ? "var(--primary)" : "rgba(255,255,255,0.85)",
                    }}
                    data-testid={`insp-swing-basket-chip-${b.templateId}`}
                    title={`Editing ${b.templateName} — click to switch back`}
                  >
                    <span className="font-black">{b.templateName}</span>
                    <span className="tabular-nums opacity-70">· {b.assetIds.length}</span>
                    <span
                      role="button"
                      tabIndex={0}
                      className="hover:text-red-400 -mr-1 ml-1 w-4 h-4 inline-flex items-center justify-center"
                      onClick={(e) => { e.stopPropagation(); removeFromBasket(b.templateId); }}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); removeFromBasket(b.templateId); } }}
                      title="Remove this inspection type from the basket"
                      data-testid={`insp-swing-basket-remove-${b.templateId}`}
                    >
                      <XIcon className="w-3 h-3" />
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Template picker — changing template no longer wipes ticks;
              previous picks live in the basket and can be edited by clicking
              their chip above. */}
          <div className="field">
            <label>{basketEntries.length === 0 ? "Type of inspection *" : `Add another inspection type${templateId ? "" : " — pick one below"}`}</label>
            <select className="input" value={templateId} onChange={(e) => setTemplateId(e.target.value)} data-testid="insp-swing-template">
              <option value="">— Select template —</option>
              {templates.map((t) => {
                const inBasket = basket[String(t.id || t._id)]?.size || 0;
                return (
                  <option key={t.id || t._id} value={t.id || t._id}>
                    {(t.name || t.title || "Template")}{t.machine_type ? ` · ${t.machine_type}` : ""}{inBasket ? ` · ${inBasket} ticked` : ""}
                  </option>
                );
              })}
            </select>
            {basketEntries.length > 0 && !templateId && (
              <div className="text-[11px] text-muted mt-1.5">
                Tip · You can add as many types as you need. Pick another template above, tick more machines, repeat. Then hit <span className="text-white font-bold">Start Swing</span>.
              </div>
            )}
          </div>

          {/* Crew picker */}
          <div className="field">
            <label className="flex items-center gap-2"><Users className="w-3.5 h-3.5" /> Assign to crew</label>
            <select className="input" value={crewId} onChange={(e) => setCrewId(e.target.value)} data-testid="insp-swing-crew">
              <option value="">— My crew (default) —</option>
              {crews.map((c) => (
                <option key={c.id || c._id} value={c.id || c._id}>{c.name || `Crew ${String(c.id || "").slice(0, 6)}`}</option>
              ))}
            </select>
          </div>

          {/* Search */}
          <div className="panel-flat px-3 flex items-center gap-2">
            <Search className="w-4 h-4 text-muted shrink-0" />
            <input
              className="w-full py-3 bg-transparent outline-none text-base font-mono-tight"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={templateId ? "Search machines matching this template…" : "Type a template first, then filter machines"}
              data-testid="insp-swing-search"
              disabled={!templateId}
            />
          </div>

          {/* Multi-select machines — always reflects the basket subset for
              the currently-selected template. Ticks persist even after
              switching template. */}
          {templateId && (
            <>
              <div className="flex items-center justify-between text-[11px] uppercase tracking-widest text-muted">
                <div>Tick each machine · {currentSelected.size} in this type · {filteredAssets.length} match{filteredAssets.length === 1 ? "" : "es"}</div>
                <div className="flex gap-2">
                  <button className="text-white/70 hover:text-white" onClick={selectAll} disabled={!filteredAssets.length} data-testid="insp-swing-select-all">Select all</button>
                  <span>·</span>
                  <button className="text-white/70 hover:text-white" onClick={clearAll} disabled={!currentSelected.size}>Clear</button>
                </div>
              </div>
              {filteredAssets.length === 0 ? (
                <div className="text-muted text-sm py-4 text-center">No matching machines. Change the template or clear the search.</div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[38vh] overflow-y-auto pr-1" data-testid="insp-swing-machine-list">
                  {filteredAssets.map((a) => {
                    const id = String(a.id || a._id);
                    const checked = currentSelected.has(id);
                    return (
                      <button
                        key={id}
                        type="button"
                        onClick={() => toggleAsset(id)}
                        className="panel p-3 text-left flex items-center gap-3"
                        style={checked ? { borderColor: "var(--primary)", boxShadow: "inset 0 0 0 1px var(--primary)" } : {}}
                        data-testid={`insp-swing-asset-${id}`}
                      >
                        <div className="w-6 h-6 shrink-0 rounded flex items-center justify-center"
                             style={{ background: checked ? "var(--primary)" : "transparent",
                                      border: checked ? "1px solid var(--primary)" : "1px solid var(--border)",
                                      color: checked ? "#0a0a0a" : "var(--muted)" }}>
                          {checked ? <Check className="w-4 h-4" strokeWidth={3} /> : null}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-display font-black text-sm truncate">
                            <span className="font-mono-tight">{a.id || id}</span>
                            {a.name && <> · {a.name}</>}
                          </div>
                          <div className="text-[11px] text-muted truncate">
                            {a.asset_type || a.category || "asset"}
                            {a.machine_model && <> · {a.machine_model}</>}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Explicit "Add another type" nudge — appears once the crew has
                  ticked at least one machine on the current template. Makes
                  the multi-type flow discoverable without a coach mark. */}
              {currentSelected.size > 0 && (
                <button
                  type="button"
                  className="btn btn-sm w-full flex items-center justify-center gap-2"
                  onClick={() => setTemplateId("")}
                  style={{
                    background: "rgba(245,130,32,0.08)",
                    border: "1px dashed rgba(245,130,32,0.5)",
                    color: "var(--primary)",
                  }}
                  data-testid="insp-swing-add-another-type"
                >
                  <PlusCircle className="w-4 h-4" />
                  Add another inspection type (Excavator + Dozer + Drill etc.)
                </button>
              )}
            </>
          )}

          {/* Per-inspection Work Order # inputs — one row per machine in
              the entire basket (across every template), grouped by type so
              the supervisor sees exactly what's about to be created. Each
              row is independent and skippable; WO# can still be edited
              later on the inspection detail. */}
          {totalMachines > 0 && (
            <div className="space-y-2" data-testid="insp-swing-wo-block">
              <div className="text-[11px] uppercase tracking-widest text-muted flex items-center gap-2">
                Work Order # <span className="text-muted/70">(optional · skip to add later)</span>
              </div>
              <div className="space-y-3 max-h-[26vh] overflow-y-auto pr-1">
                {basketEntries.map((b) => (
                  <div key={b.templateId}>
                    <div className="text-[10px] uppercase tracking-widest font-black mb-1" style={{ color: "var(--primary)" }}>
                      {b.templateName} · {b.assetIds.length} machine{b.assetIds.length === 1 ? "" : "s"}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {b.assetIds.map((aid) => {
                        const asset = assets.find((a) => String(a.id || a._id) === String(aid));
                        const label = asset ? `${asset.id || aid}${asset.name ? " · " + asset.name : ""}` : aid;
                        return (
                          <div
                            key={aid}
                            className="panel-flat p-2 flex items-center gap-2"
                            data-testid={`insp-swing-wo-row-${aid}`}
                          >
                            <div className="flex-1 min-w-0">
                              <div className="text-[10px] uppercase tracking-widest text-muted truncate">{label}</div>
                              <input
                                type="text"
                                className="input !min-h-[32px] !py-1 !text-xs font-mono-tight mt-1"
                                placeholder="WO-… (or skip)"
                                value={woByAsset[aid] || ""}
                                onChange={(e) => setWoByAsset((m) => ({ ...m, [aid]: e.target.value }))}
                                maxLength={40}
                                data-testid={`insp-swing-wo-input-${aid}`}
                              />
                            </div>
                            {(woByAsset[aid] || "").trim() && (
                              <button
                                type="button"
                                className="btn btn-xs btn-ghost !py-0.5 !px-1.5 shrink-0"
                                onClick={() => setWoByAsset((m) => { const n = { ...m }; delete n[aid]; return n; })}
                                title="Clear (skip)"
                                data-testid={`insp-swing-wo-skip-${aid}`}
                              >
                                <XIcon className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {msg && (
            <div className={`pill ${msg.type === "ok" ? "pill-green" : "pill-red"} !text-xs !py-2 !px-3 justify-start`} data-testid="insp-swing-msg">
              {msg.type === "ok" ? <Check className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />} {msg.text}
            </div>
          )}
        </div>

        <div className="p-4 flex items-center justify-between gap-3" style={{ borderTop: "1px solid var(--border)" }}>
          <div className="text-sm min-w-0">
            <span className="font-display font-black text-2xl" style={{ color: "var(--primary)" }}>{totalMachines}</span>
            <span className="text-muted ml-2">
              inspection{totalMachines === 1 ? "" : "s"} across {basketEntries.length} type{basketEntries.length === 1 ? "" : "s"}
            </span>
          </div>
          <div className="flex gap-2">
            <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button
              className="btn btn-primary"
              style={{ minHeight: 52, fontSize: 15, padding: "0 22px" }}
              onClick={submit}
              disabled={totalMachines === 0 || busy}
              data-testid="insp-swing-submit"
            >
              {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <PlayCircle className="w-5 h-5" />}
              {activeSwing ? `Add ${totalMachines || ""} to Crew`.trim() : `Start Swing${totalMachines ? ` · ${totalMachines}` : ""}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- helpers ---------------- */
const TONE = {
  approved: { bg: "#0a2a12", border: "#1e6b32", color: "#4ade80" },
  submitted: { bg: "#1a2a55", border: "#3b60d1", color: "#8bb4ff" },
  progress:  { bg: "#4a2a06", border: "#f58220", color: "#ffb347" },
  back:      { bg: "#5a0f0f", border: "#a51919", color: "#ff9b9b" },
  idle:      { bg: "#141414", border: "#2a2a2a", color: "#a1a1aa" },
};
function isActive(it) {
  const s = String(it.status || it.stage || "").toLowerCase();
  return !s.includes("approve") && !s.includes("closed") && !s.includes("complete") && !s.includes("signed");
}
function progressOf(it) {
  const s = String(it.status || it.stage || "").toLowerCase();
  // Prefer the server-side item_summary the mobile app also reads.
  // answered = total - open  →  pct = answered / total.
  const sum = it.item_summary || {};
  const total = Number(sum.total || 0);
  const open = Number(sum.open || 0);
  let pct = null;
  if (total > 0) {
    const answered = Math.max(0, total - open);
    pct = Math.round((answered / total) * 100);
  } else if (typeof it.progress === "number") {
    pct = clamp(it.progress);
  } else if (typeof it.completion === "number") {
    pct = clamp(it.completion);
  } else if (Array.isArray(it.items) && it.items.length) {
    const done = it.items.filter((x) => x.status || (x.value != null && x.value !== "")).length;
    pct = Math.round((done / it.items.length) * 100);
  }

  // Terminal states — always 100%.
  if (s.includes("approve") || s.includes("closed") || s.includes("complete") || s.includes("signed"))
    return { pct: 100, label: "Approved", tone: "approved", icon: <CheckCircle2 className="w-5 h-5" /> };
  if (s.includes("submit") || s.includes("ready") || s.includes("pending"))
    return { pct: pct ?? 100, label: "Submitted", tone: "submitted", icon: <PenTool className="w-5 h-5" /> };
  if (s.includes("send") && s.includes("back"))
    return { pct: pct ?? 0, label: "Sent back", tone: "back", icon: <AlertTriangle className="w-5 h-5" /> };

  // Anything with real answered items → In progress at its true %.
  if ((pct ?? 0) > 0)
    return { pct, label: "In progress", tone: "progress", icon: <Loader2 className="w-5 h-5 animate-spin" /> };

  // draft / planned / open with zero answered items → Not started (matches mobile).
  return { pct: 0, label: "Not started", tone: "idle", icon: <Clock className="w-5 h-5" /> };
}

// Derive the shift ("day" | "night") from a server swing object. The
// backend doesn't currently expose an explicit `shift` field, so we key off
// the human-readable `swing_label` ("Swing · Week 27 · 2026-07-05 · Day").
function deriveSwingShift(swing) {
  if (!swing) return null;
  if (swing.shift) return String(swing.shift).toLowerCase();
  const label = String(swing.swing_label || swing.notes || "").toLowerCase();
  if (label.includes("night")) return "night";
  if (label.includes("day")) return "day";
  return null;
}

function isThisShift(it, shift, user) {
  const site = user?.site_code;
  const siteMatch = !site || !it.site_code || String(it.site_code) === String(site);
  if (!siteMatch) return false;

  // If the inspection carries a shift tag and it doesn't match the current
  // shift, exclude it — mobile app enforces this.
  if (it.shift && String(it.shift).toLowerCase() !== shift) return false;

  const s = String(it.status || "").toLowerCase();
  const terminal = /approve|closed|complete|signed/.test(s);

  // Primary date the mobile app keys off is `assigned_at` — the moment the
  // planner attached this inspection to a swing. `created_at` / `updated_at`
  // both leak too many stale items (updated_at bumps whenever ANY field
  // touches, e.g. via polling refresh).
  const referenceTs =
    (terminal ? (it.approved_at || it.completed_at) : it.assigned_at) ||
    it.assigned_at || it.updated_at || it.created_at;
  if (!referenceTs) return false;

  const d = new Date(referenceTs);
  const now = new Date();
  const hoursAgo = (now.getTime() - d.getTime()) / 3_600_000;

  // Recency window — the mobile app keeps inspections that are still in
  // flight for ~48 hours after assignment. For a strict "this shift" window
  // that mirrors mobile:
  //   • day shift  → last 24 h
  //   • night shift → last 24 h (which naturally spans yesterday evening
  //                              through this morning)
  // We widen non-terminal items to 48 h so a job that spilled from the
  // previous shift into today still shows.
  const window = terminal ? 24 : 48;
  return hoursAgo >= 0 && hoursAgo <= window;
}
function currentShift() {
  // Legacy fallback used only for the client-side "this shift" inspection filter
  // when the site-config hook hasn't resolved yet. Day/Night matching the
  // real config is done via useSiteShift().
  const h = new Date().getHours();
  return (h >= 6 && h < 18) ? "day" : "night";
}
function shiftLabel(s) { return ({ morning: "Morning", afternoon: "Afternoon", night: "Night", day: "Day" }[s] || "Current"); }
function clamp(n) { return Math.max(0, Math.min(100, Math.round(n <= 1 ? n * 100 : n))); }
function norm(v) { return String(v || "").toLowerCase().replace(/[_\-\s]+/g, ""); }
function isSupervisor(u) {
  const r = String(u?.role || "").toLowerCase();
  return u?.is_admin || ["admin", "control", "owner", "supervisor", "superintendent", "planner"].some((k) => r.includes(k));
}
/** ISO-8601 week number: Thursday-based, Jan 4 is always in week 1. */
function weekOfYear(date = new Date()) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
}
function fmtRel(v) {
  try {
    const s = (Date.now() - new Date(v).getTime()) / 1000;
    if (s < 60) return "just now";
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
  } catch { return ""; }
}
function fmtTime(v) {
  try { return new Date(v).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
}
